import React, { useState, useEffect, useCallback } from 'react';
import { useNavigation } from '../context/NavigationContext';
import { cldThumb } from '../utils/img';
import Modal from '../components/Modal';
import {
  preEntry, getPendingItems, receiveItem, rejectItem,
  bulkReceive, putawayItem, bulkPutaway,
  getRejectedItems, getAwaitingPutaway,
  markMissing, downloadRejectedReport
} from '../api/inbound';
import { useSizes } from '../api/meta';
import Warehouse from './Warehouse';

const TEMPLATE_COLUMNS = [
  'design_code','design_name','brand_name','category','gender','color',
  'size','brand_size','quantity','mrp','cost_price',
  'starting_zoddle_price','zoddle_price','saver_price',
  'cost_price_with_tax','launch_price',
  'ownership_type','use_case_tags','style_tags','material',
  'hsn_code','description','image_url_1','image_url_2',
  'image_url_3','image_url_4','brand_barcode',
  'sor_start_date','sor_end_date'
];

const TEMPLATE_EXAMPLE = {
  design_code: '', design_name: 'Floral Frock', brand_name: 'Hopscotch',
  category: 'dress', gender: 'girl', color: 'Pink', size: '3-4Y',
  brand_size: '3-4 Girl', quantity: '5', mrp: '799', cost_price: '400',
  starting_zoddle_price: '649', zoddle_price: '649', saver_price: '599',
  cost_price_with_tax: '', launch_price: '',
  ownership_type: 'standard', use_case_tags: 'partywear;casualwear',
  style_tags: 'floral_print;bright_colours', material: '100% Cotton',
  hsn_code: '6209', description: '', image_url_1: '', image_url_2: '',
  image_url_3: '', image_url_4: '', brand_barcode: '',
  sor_start_date: '', sor_end_date: '',
};

const TEMPLATE_REORDER_EXAMPLE = {
  ...TEMPLATE_EXAMPLE, design_code: '00-1', quantity: '3',
};

function downloadTemplate() {
  const header = TEMPLATE_COLUMNS.join(',');
  const ex = TEMPLATE_COLUMNS.map(c => TEMPLATE_EXAMPLE[c] || '').join(',');
  const re = TEMPLATE_COLUMNS.map(c => TEMPLATE_REORDER_EXAMPLE[c] || '').join(',');
  const blob = new Blob([`${header}\n${ex}\n${re}`], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = 'zoddle_inbound_template.csv'; a.click();
  URL.revokeObjectURL(url);
}

function csvEscape(v) {
  const s = String(v ?? '');
  // Wrap in quotes if it contains a separator/quote/newline; escape quotes by doubling.
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function parseCSV(text) {
  const lines = text.trim().split('\n');
  if (lines.length < 2) return [];
  const headers = lines[0].split(',').map(h => h.trim());
  return lines.slice(1).map(line => {
    const values = line.split(',').map(v => v.trim());
    const obj = {};
    headers.forEach((h, i) => { obj[h] = values[i] || ''; });
    return obj;
  });
}

function rowToPayload(row) {
  return {
    design_code: row.design_code || null,
    design_name: row.design_name, brand_name: row.brand_name,
    category: row.category, gender: row.gender, color: row.color,
    size: row.size, brand_size: row.brand_size || null,
    quantity: parseInt(row.quantity) || 1,
    mrp: parseFloat(row.mrp), cost_price: parseFloat(row.cost_price),
    starting_zoddle_price: parseFloat(row.starting_zoddle_price),
    zoddle_price: parseFloat(row.zoddle_price),
    saver_price: parseFloat(row.saver_price),
    cost_price_with_tax: row.cost_price_with_tax ? parseFloat(row.cost_price_with_tax) : null,
    launch_price: row.launch_price ? parseFloat(row.launch_price) : null,
    ownership_type: row.ownership_type || 'standard',
    use_case_tags: row.use_case_tags || null, style_tags: row.style_tags || null,
    material: row.material || null, hsn_code: row.hsn_code || null,
    description: row.description || null,
    image_url_1: row.image_url_1 || null, image_url_2: row.image_url_2 || null,
    image_url_3: row.image_url_3 || null, image_url_4: row.image_url_4 || null,
    brand_barcode: row.brand_barcode || null,
    sor_start_date: row.sor_start_date || null, sor_end_date: row.sor_end_date || null,
  };
}

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  function handleCopy() {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }
  return (
    <button onClick={handleCopy} className="text-xs text-zoddle-blue hover:opacity-70 font-mono">
      {copied ? '✓ copied' : text}
    </button>
  );
}

export default function Inbound() {
  const { navData, setSubTab } = useNavigation();
  const [activeTab, setActiveTab] = useState(navData?.subTab || 'preentry');

  // ⌘K deep link: navigate('inbound', { subTab }).
  useEffect(() => {
    if (navData?.subTab) setActiveTab(navData.subTab);
  }, [navData]);

  useEffect(() => { setSubTab('inbound', activeTab); }, [activeTab, setSubTab]);
  const zoddleSizes = useSizes();

  // Pre-entry state
  const [uploadFile, setUploadFile] = useState(null);
  const [parsedRows, setParsedRows] = useState([]);
  const [batchName, setBatchName] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [uploadError, setUploadError] = useState('');
  // Snapshots kept after submit so the user can download a *_with_barcode.csv:
  const [uploadOriginalRows, setUploadOriginalRows] = useState([]);
  const [uploadOriginalFilename, setUploadOriginalFilename] = useState('');

  // Receiving state
  const [batches, setBatches] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState('');
  const [pendingItems, setPendingItems] = useState([]);
  const [receivingLoading, setReceivingLoading] = useState(false);
  const [bulkReceiving, setBulkReceiving] = useState(false);
  const [bulkReceiveResult, setBulkReceiveResult] = useState(null);
  const [itemActions, setItemActions] = useState({});

  // Putaway state
  const [putawayItems, setPutawayItems] = useState([]);
  const [putawayLoading, setPutawayLoading] = useState(false);
  const [putawayFilter, setPutawayFilter] = useState({ size: '', gender: '', inbound_batch: '' });
  const [putawayBatches, setPutawayBatches] = useState([]);
  const [puttingAway, setPuttingAway] = useState({});  // item_id -> true while in progress
  const [bulkPutawayAllRunning, setBulkPutawayAllRunning] = useState(false);
  const [bulkPutawayAllResult, setBulkPutawayAllResult] = useState(null);
  const [bulkPutawayFileResult, setBulkPutawayFileResult] = useState(null);
  const [bulkPutawayFileRunning, setBulkPutawayFileRunning] = useState(false);

  // Rejected state
  const [rejectedItems, setRejectedItems] = useState([]);
  const [rejectedLoading, setRejectedLoading] = useState(false);
  const [rejectedBatchFilter, setRejectedBatchFilter] = useState('');
  const [allBatches, setAllBatches] = useState([]);

  // Load batches for receiving
  const loadBatches = useCallback(async () => {
    try {
      const items = await getPendingItems();
      const batchSet = new Set(items.map(i => i.inbound_batch).filter(Boolean));
      setBatches([...batchSet]);
    } catch (e) { console.error(e); }
  }, []);

  // Load all batches for filters
  const loadAllBatches = useCallback(async () => {
    try {
      const rejected = await getRejectedItems();
      const pending = await getPendingItems();
      const putaway = await getAwaitingPutaway();
      const batchSet = new Set([
        ...rejected.items.map(i => i.inbound_batch),
        ...pending.map(i => i.inbound_batch),
        ...putaway.items.map(i => i.inbound_batch),
      ].filter(Boolean));
      const sorted = [...batchSet].sort((a, b) => b.localeCompare(a));
      setAllBatches(sorted);
      setPutawayBatches(sorted);
    } catch (e) { console.error(e); }
  }, []);

  // Load pending items for selected batch
  const loadPendingItems = useCallback(async () => {
    if (!selectedBatch) return;
    setReceivingLoading(true);
    try {
      const items = await getPendingItems(selectedBatch);
      setPendingItems(items);
      setItemActions({});
    } catch (e) { console.error(e); }
    finally { setReceivingLoading(false); }
  }, [selectedBatch]);

  // Load awaiting putaway items
  const loadPutawayItems = useCallback(async () => {
    setPutawayLoading(true);
    try {
      const filters = {};
      if (putawayFilter.size) filters.size = putawayFilter.size;
      if (putawayFilter.gender) filters.gender = putawayFilter.gender;
      if (putawayFilter.inbound_batch) filters.inbound_batch = putawayFilter.inbound_batch;
      const data = await getAwaitingPutaway(filters);
      setPutawayItems(data.items || []);
    } catch (e) { console.error(e); }
    finally { setPutawayLoading(false); }
  }, [putawayFilter]);

  // Load rejected items
  const loadRejectedItems = useCallback(async () => {
    setRejectedLoading(true);
    try {
      const data = await getRejectedItems(rejectedBatchFilter || null);
      setRejectedItems(data.items || []);
    } catch (e) { console.error(e); }
    finally { setRejectedLoading(false); }
  }, [rejectedBatchFilter]);

  useEffect(() => { loadBatches(); loadAllBatches(); }, [loadBatches, loadAllBatches]);
  useEffect(() => { loadPendingItems(); }, [loadPendingItems]);
  useEffect(() => { if (activeTab === 'putaway') loadPutawayItems(); }, [activeTab, loadPutawayItems]);
  useEffect(() => { if (activeTab === 'rejected') loadRejectedItems(); }, [activeTab, loadRejectedItems]);
  useEffect(() => { if (activeTab === 'rejected') loadRejectedItems(); }, [rejectedBatchFilter]);

  // Pre-entry handlers
  function handleFileSelect(e) {
    const file = e.target.files[0];
    if (!file) return;
    setUploadFile(file);
    setUploadResult(null);
    setUploadError('');
    setBatchName(file.name.replace(/\.[^.]+$/, ''));
    const reader = new FileReader();
    reader.onload = (ev) => { setParsedRows(parseCSV(ev.target.result)); };
    reader.readAsText(file);
  }

  function downloadWithBarcodeCsv() {
    if (!uploadResult || !uploadOriginalRows.length) return;
    // Original headers in the uploaded file, then 8 appended columns.
    const originalHeaders = Object.keys(uploadOriginalRows[0]);
    const appendedHeaders = [
      'zoddle_barcode','brand_name','zoddle_size',
      'mrp','zoddle_price','units_in_the_piece','gender','discounted',
    ];
    const allHeaders = [...originalHeaders, ...appendedHeaders];

    // One output row per physical item. uploadResult[i] corresponds to
    // uploadOriginalRows[i]; barcodes within an entry are in creation order.
    const lines = [allHeaders.join(',')];
    uploadOriginalRows.forEach((row, i) => {
      const resp = uploadResult[i];
      if (!resp) return;
      (resp.zoddle_barcodes || []).forEach(bc => {
        const original = originalHeaders.map(h => csvEscape(row[h] ?? ''));
        const appended = [
          csvEscape(bc),
          csvEscape(resp.brand_name || ''),
          csvEscape(resp.size || ''),
          csvEscape(resp.mrp ?? row.mrp ?? ''),
          csvEscape(resp.zoddle_price ?? row.zoddle_price ?? ''),
          '0',
          csvEscape(resp.gender || row.gender || ''),
          csvEscape(resp.discounted ?? ''),
        ];
        lines.push([...original, ...appended].join(','));
      });
    });

    const csv = lines.join('\n');
    const base = uploadOriginalFilename.replace(/\.[^.]+$/, '');
    const filename = `${base}_with_barcode.csv`;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a); URL.revokeObjectURL(url);
  }

  async function handleUpload() {
    if (!parsedRows.length) return;
    setUploading(true); setUploadError('');
    try {
      const result = await preEntry(parsedRows.map(rowToPayload), batchName);
      // Snapshot rows + filename BEFORE clearing so the download button works.
      setUploadOriginalRows(parsedRows);
      setUploadOriginalFilename(uploadFile?.name || `${batchName}.csv`);
      setUploadResult(result);
      setParsedRows([]); setUploadFile(null);
      loadBatches(); loadAllBatches();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (Array.isArray(detail)) {
        // FastAPI 422: array of {type, loc, msg, input}.
        // Convert to readable per-row lines so the user can fix everything in one pass.
        const lines = detail.slice(0, 30).map(d => {
          const loc = d.loc || [];
          if (loc[1] === 'items' && typeof loc[2] === 'number') {
            return `Row ${loc[2] + 1} — ${loc[3] || '?'}: ${d.msg}`;
          }
          return `${loc.slice(1).join('.') || 'request'}: ${d.msg}`;
        });
        const header = `Validation failed (${detail.length} error${detail.length === 1 ? '' : 's'}):`;
        const overflow = detail.length > 30 ? `\n…and ${detail.length - 30} more` : '';
        setUploadError(header + '\n' + lines.join('\n') + overflow);
      } else if (typeof detail === 'string') {
        setUploadError(detail);
      } else {
        setUploadError('Upload failed.');
      }
    }
    finally { setUploading(false); }
  }

  // Receiving handlers
  async function handleAccept(item) {
    try {
      await receiveItem(item.item_id, item.zoddle_barcode);
      setItemActions(prev => ({ ...prev, [item.item_id]: 'received' }));
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
  }

  async function handleReject(item) {
    if (!window.confirm('Mark as damaged on arrival?')) return;
    try {
      await rejectItem(item.item_id);
      setItemActions(prev => ({ ...prev, [item.item_id]: 'rejected' }));
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
  }

  async function handleMissing(item) {
    if (!window.confirm('Mark as missing on arrival? (Brand sent fewer than expected.)')) return;
    try {
      await markMissing(item.item_id);
      setItemActions(prev => ({ ...prev, [item.item_id]: 'missing' }));
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
  }

  function handlePrint() {
    const esc = (v) => String(v ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    const thumb = (u) => cldThumb(u, 240);
    const cards = pendingItems.map(i => {
      const img = thumb(i.image_url_1);
      const bb = i.brand_barcode ? ` (${esc(i.brand_barcode)})` : '';
      const bs = i.brand_size ? ` (${esc(i.brand_size)})` : '';
      const mrp = (i.mrp !== null && i.mrp !== undefined && i.mrp !== '') ? `₹${esc(i.mrp)}` : '';
      const l2 = `${esc(i.zoddle_size || '')}${bs}${mrp ? `, ${mrp}` : ''}`;
      return `<div class="card">${img ? `<img src="${esc(img)}" />` : '<div class="noimg">no image</div>'}` +
             `<div class="l1">${esc(i.zoddle_barcode || '')}${bb}</div><div class="l2">${l2}</div></div>`;
    }).join('');
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>${esc(selectedBatch)}</title><style>
*{box-sizing:border-box}body{font-family:Arial,sans-serif;margin:8px}h1{font-size:12px;margin:0 0 8px}
.grid{display:grid;grid-template-columns:repeat(6,1fr);gap:5px}
.card{border:1px solid #ddd;border-radius:4px;padding:4px;text-align:center;page-break-inside:avoid}
.card img{width:100%;height:88px;object-fit:contain}
.noimg{height:88px;display:flex;align-items:center;justify-content:center;color:#bbb;font-size:9px}
.l1{font-size:8px;font-weight:600;margin-top:2px;word-break:break-all;line-height:1.15}
.l2{font-size:8px;color:#444;line-height:1.15}
@page{margin:8mm}</style></head><body>
<h1>${esc(selectedBatch)} — ${pendingItems.length} items</h1><div class="grid">${cards}</div>
<script>window.onload=function(){setTimeout(function(){window.print();},300)};</script></body></html>`;
    const w = window.open('', '_blank');
    if (!w) { alert('Please allow pop-ups to print.'); return; }
    w.document.write(html); w.document.close();
  }

  async function handleBulkReceive() {
    if (!window.confirm(`Bulk receive all ${pendingCount} remaining items?`)) return;
    setBulkReceiving(true); setBulkReceiveResult(null);
    try {
      const itemIds = pendingItems.filter(i => !itemActions[i.item_id]).map(i => i.item_id);
      const result = await bulkReceive(itemIds);
      setBulkReceiveResult(result);
      const newActions = {};
      result.received.forEach(r => { newActions[r.item_id] = 'received'; });
      setItemActions(prev => ({ ...prev, ...newActions }));
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
    finally { setBulkReceiving(false); }
  }

  // Putaway handlers
  async function handlePutawayOne(item) {
    if (!item.suggested_bin_id) {
      alert('No bin suggestion available for this item. Assign manually via CSV.');
      return;
    }
    setPuttingAway(prev => ({ ...prev, [item.item_id]: true }));
    try {
      await putawayItem(item.item_id, item.suggested_bin_id);
      setPutawayItems(prev => prev.filter(i => i.item_id !== item.item_id));
    } catch (e) { alert(e.response?.data?.detail || 'Putaway failed'); }
    finally { setPuttingAway(prev => ({ ...prev, [item.item_id]: false })); }
  }

  async function handleBulkPutawayAll() {
    const itemsWithSuggestion = putawayItems.filter(i => i.suggested_bin_id);
    if (!itemsWithSuggestion.length) { alert('No items with bin suggestions.'); return; }
    if (!window.confirm(`Put away all ${itemsWithSuggestion.length} items using suggested bins?`)) return;
    setBulkPutawayAllRunning(true); setBulkPutawayAllResult(null);
    try {
      const result = await bulkPutaway(
        itemsWithSuggestion.map(i => ({ item_id: i.item_id, bin_id: i.suggested_bin_id }))
      );
      setBulkPutawayAllResult(result);
      loadPutawayItems();
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
    finally { setBulkPutawayAllRunning(false); }
  }

  async function handleBulkPutawayFile(e) {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (ev) => {
      const rows = parseCSV(ev.target.result);
      const items = rows.map(r => ({ item_id: r.item_id, bin_id: r.bin_id || null })).filter(r => r.item_id);
      setBulkPutawayFileRunning(true);
      try {
        const result = await bulkPutaway(items);
        setBulkPutawayFileResult(result);
        loadPutawayItems();
      } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
      finally { setBulkPutawayFileRunning(false); }
    };
    reader.readAsText(file);
  }

  function downloadPutawayTemplate() {
    const blob = new Blob(['item_id,bin_id\nexample-id,example-bin-id'], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'zoddle_putaway_template.csv'; a.click();
    URL.revokeObjectURL(url);
  }

  const receivedCount = Object.values(itemActions).filter(a => a === 'received').length;
  const rejectedCount = Object.values(itemActions).filter(a => a === 'rejected').length;
  const pendingCount = pendingItems.length - receivedCount - rejectedCount;

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-6">
        <h2 className="text-2xl font-black text-gray-800">Inbounding</h2>
        <p className="text-gray-500 text-sm mt-1">Pre-entry, receiving, putaway and rejected items</p>
      </div>

      <div className="flex gap-1 mb-6 bg-gray-100 rounded-xl p-1 w-fit">
        {[
          { key: 'preentry', label: 'Pre-Entry' },
          { key: 'receiving', label: 'Receiving' },
          { key: 'putaway', label: 'Putaway' },
          { key: 'rejected', label: 'Rejected' },
          { key: 'warehouse', label: 'Warehouse' },
        ].map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)}
            className={`px-6 py-2 rounded-lg text-sm font-semibold transition-all
              ${activeTab === tab.key ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* PRE-ENTRY TAB */}
      {activeTab === 'preentry' && (
        <div className="max-w-2xl">
          <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 mb-6">
            <div className="flex items-center justify-between mb-2">
              <div>
                <p className="text-sm font-semibold text-gray-700">Download Inbound Template</p>
                <p className="text-xs text-gray-500 mt-0.5">CSV with all columns + 2 example rows</p>
              </div>
              <button onClick={downloadTemplate}
                className="text-sm text-zoddle-blue font-semibold border border-zoddle-blue rounded-lg px-4 py-2 hover:bg-blue-50">
                Download
              </button>
            </div>
            <div className="bg-white rounded-lg p-3 mt-2 border border-blue-100">
              <p className="text-xs font-semibold text-gray-600 mb-1">💡 Using design_code for reorders</p>
              <p className="text-xs text-gray-500">
                Leave <span className="font-mono bg-gray-100 px-1 rounded">design_code</span> blank for new items.
                For reorders fill the existing code e.g. <span className="font-mono bg-gray-100 px-1 rounded">00-1</span>.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="font-bold text-gray-800 mb-4">Upload Inbound Spreadsheet</h3>
            <div className="mb-4">
              <label className="block text-gray-600 text-sm mb-1">Select CSV File</label>
              <input type="file" accept=".csv" onChange={handleFileSelect}
                className="w-full text-sm text-gray-600 border border-gray-200 rounded-lg px-3 py-2 bg-gray-50
                           file:mr-3 file:py-1 file:px-3 file:rounded-lg file:border-0 file:text-sm
                           file:font-semibold file:bg-zoddle-green file:text-white hover:file:opacity-90" />
            </div>
            {uploadFile && (
              <div className="mb-4">
                <label className="block text-gray-600 text-sm mb-1">Batch Name</label>
                <input type="text" value={batchName} onChange={(e) => setBatchName(e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 bg-gray-50 focus:outline-none focus:border-zoddle-green" />
                <p className="text-xs text-gray-400 mt-1">Auto-generated from filename — edit if needed</p>
              </div>
            )}
            {parsedRows.length > 0 && (
              <div className="mb-4 bg-gray-50 rounded-lg p-3">
                <p className="text-sm font-semibold text-gray-700 mb-2">Preview — {parsedRows.length} rows</p>
                <div className="overflow-x-auto">
                  <table className="text-xs w-full">
                    <thead>
                      <tr className="text-gray-500">
                        {['Code','Design','Brand','Size','Qty','MRP','Zoddle ₹'].map(h => (
                          <th key={h} className="text-left pr-3 py-1">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {parsedRows.slice(0,5).map((row,i) => (
                        <tr key={i} className="border-t border-gray-100">
                          <td className="pr-3 py-1 font-mono text-zoddle-blue">{row.design_code||'—'}</td>
                          <td className="pr-3 py-1 text-gray-700">{row.design_name}</td>
                          <td className="pr-3 py-1 text-gray-700">{row.brand_name}</td>
                          <td className="pr-3 py-1 text-gray-700">{row.size}</td>
                          <td className="pr-3 py-1 text-gray-700">{row.quantity}</td>
                          <td className="pr-3 py-1 text-gray-700">₹{row.mrp}</td>
                          <td className="pr-3 py-1 text-gray-700">₹{row.zoddle_price}</td>
                        </tr>
                      ))}
                      {parsedRows.length > 5 && (
                        <tr><td colSpan="7" className="text-gray-400 py-1">+{parsedRows.length-5} more...</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            {uploadError && (
              <div className="text-zoddle-pink text-sm mb-3 whitespace-pre-line max-h-64 overflow-auto border border-zoddle-pink/30 rounded p-2 bg-zoddle-pink/5">
                {uploadError}
              </div>
            )}
            {parsedRows.length > 0 && (
              <button onClick={handleUpload} disabled={uploading||!batchName}
                className="w-full bg-zoddle-green text-white font-bold py-3 rounded-lg hover:opacity-90 disabled:opacity-50">
                {uploading ? 'Uploading and copying images...' : `Submit ${parsedRows.length} Rows as Batch "${batchName}"`}
              </button>
            )}
          </div>

          {uploadResult && (
            <div className="mt-4 bg-green-50 border border-green-100 rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <p className="font-semibold text-green-800">✓ Batch created successfully</p>
                {uploadOriginalRows.length > 0 && (
                  <button
                    onClick={downloadWithBarcodeCsv}
                    className="text-sm bg-zoddle-blue text-white font-semibold px-4 py-2 rounded-lg hover:opacity-90"
                  >
                    Download {(uploadOriginalFilename || 'batch').replace(/\.[^.]+$/, '')}_with_barcode.csv
                  </button>
                )}
              </div>
              {(() => {
                const notCopied = uploadResult.reduce((n, r) => n + (r.warnings || []).filter(w => w.includes('not copied to S3')).length, 0);
                return notCopied > 0 && (
                  <p className="text-sm text-yellow-800 bg-yellow-50 border border-yellow-200 rounded-lg px-3 py-2 mb-2">
                    {notCopied} image link{notCopied === 1 ? '' : 's'} could not be copied to S3 and kept the original link — see the rows below.
                  </p>
                );
              })()}
              <div className="space-y-2">
                {uploadResult.map((r,i) => (
                  <div key={i} className="bg-white rounded-lg px-4 py-3 border border-green-100">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs bg-gray-100 px-2 py-0.5 rounded text-gray-600">
                          <CopyButton text={r.design_code||'—'} />
                        </span>
                        <span className="font-semibold text-gray-800 text-sm">{r.design_name}</span>
                        <span className="text-gray-400 text-xs">{r.size}</span>
                      </div>
                      <span className="text-xs text-gray-500">{r.items_created} items</span>
                    </div>
                    <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                      <span className="text-xs text-gray-500">{r.brand_name}</span>
                      {r.brand_status==='created_new_brand' && <span className="text-xs bg-blue-50 text-zoddle-blue px-2 py-0.5 rounded-full font-medium">New brand</span>}
                      {r.design_status==='created_new_design' && <span className="text-xs bg-purple-50 text-purple-600 px-2 py-0.5 rounded-full font-medium">New design</span>}
                      {r.design_status==='used_existing_design' && <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full font-medium">Existing design</span>}
                    </div>
                    {r.warnings&&r.warnings.map((w,wi) => (
                      <p key={wi} className="text-xs text-yellow-700 bg-yellow-50 rounded px-2 py-1 mt-1">{w}</p>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* RECEIVING TAB */}
      {activeTab === 'receiving' && (
        <div>
          <div className="flex items-center gap-3 mb-6">
            <select value={selectedBatch} onChange={(e) => setSelectedBatch(e.target.value)}
              className="text-sm border border-gray-200 rounded-lg px-3 py-2 text-gray-600 bg-white focus:outline-none focus:border-zoddle-green">
              <option value="">Select a batch</option>
              {batches.map(b => <option key={b} value={b}>{b}</option>)}
            </select>
            {selectedBatch && pendingItems.length > 0 && (
              <button onClick={handleBulkReceive} disabled={bulkReceiving||pendingCount===0}
                className="bg-zoddle-blue text-white text-sm font-semibold px-4 py-2 rounded-lg hover:opacity-90 disabled:opacity-50">
                {bulkReceiving ? 'Receiving...' : `Bulk Receive All (${pendingCount} remaining)`}
              </button>
            )}
            {selectedBatch && pendingItems.length > 0 && (
              <button onClick={handlePrint}
                className="bg-gray-700 text-white text-sm font-semibold px-4 py-2 rounded-lg hover:opacity-90">
                Print
              </button>
            )}
          </div>
          {selectedBatch && pendingItems.length > 0 && (
            <div className="flex items-center gap-4 mb-4">
              <span className="text-sm text-gray-500">Total: <strong>{pendingItems.length}</strong></span>
              <span className="text-sm text-zoddle-green">Received: <strong>{receivedCount}</strong></span>
              <span className="text-sm text-zoddle-pink">Rejected: <strong>{rejectedCount}</strong></span>
              <span className="text-sm text-gray-400">Pending: <strong>{pendingCount}</strong></span>
            </div>
          )}
          {bulkReceiveResult && (
            <div className="mb-4 bg-green-50 border border-green-100 rounded-xl p-4">
              <p className="font-semibold text-green-800">✓ {bulkReceiveResult.total_received} items received</p>
              {bulkReceiveResult.total_skipped > 0 && <p className="text-sm text-yellow-700 mt-1">{bulkReceiveResult.total_skipped} skipped</p>}
            </div>
          )}
          {!selectedBatch ? (
            <p className="text-gray-400 text-sm">Select a batch to start receiving.</p>
          ) : receivingLoading ? (
            <p className="text-gray-400 text-sm">Loading items...</p>
          ) : pendingItems.length === 0 ? (
            <p className="text-gray-400 text-sm">No pending items in this batch.</p>
          ) : (
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100">
                    {['Image','Barcode','Brand · Design','Size','MRP','Status','Actions'].map(h => (
                      <th key={h} className="text-left px-4 py-3 text-gray-500 font-semibold">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pendingItems.map(item => {
                    const action = itemActions[item.item_id];
                    const sizeLabel = item.brand_size
                      ? `${item.zoddle_size || '—'} (${item.brand_size})`
                      : (item.zoddle_size || '—');
                    const barcodeLabel = item.brand_barcode
                      ? `${item.zoddle_barcode || '—'} (${item.brand_barcode})`
                      : (item.zoddle_barcode || '—');
                    const rowBg = action==='received' ? 'bg-green-50'
                                : action==='rejected' ? 'bg-red-50'
                                : action==='missing'  ? 'bg-amber-50'
                                : '';
                    return (
                      <tr key={item.item_id}
                        className={`border-b border-gray-50 transition-colors ${rowBg}`}>
                        <td className="px-4 py-3">
                          {item.image_url_1 ? (
                            <a href={item.image_url_1} target="_blank" rel="noopener noreferrer" title="Open full image">
                              <img src={cldThumb(item.image_url_1, 300)} alt={item.design_name || ''}
                                className="w-12 h-12 object-cover rounded border border-gray-200 hover:opacity-80"
                                loading="lazy"
                                onError={e => { e.currentTarget.style.display = 'none'; }} />
                            </a>
                          ) : <span className="text-gray-300 text-xs">—</span>}
                        </td>
                        <td className="px-4 py-3 text-xs font-mono text-gray-700">{barcodeLabel}</td>
                        <td className="px-4 py-3 text-gray-700">
                          {item.design_name ? (
                            <>
                              <div className="text-xs text-gray-500">{item.brand_name || '—'}</div>
                              <div className="font-medium">{item.design_name}</div>
                            </>
                          ) : <span className="text-gray-300 text-xs">—</span>}
                        </td>
                        <td className="px-4 py-3 text-gray-700 text-xs">{sizeLabel}</td>
                        <td className="px-4 py-3 text-gray-700">₹{item.mrp}</td>
                        <td className="px-4 py-3">
                          {action==='received' && <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-medium">Received</span>}
                          {action==='rejected' && <span className="text-xs bg-red-100 text-zoddle-pink px-2 py-1 rounded-full font-medium">Rejected</span>}
                          {action==='missing'  && <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded-full font-medium">Missing</span>}
                          {!action && <span className="text-xs bg-gray-100 text-gray-500 px-2 py-1 rounded-full">Pending</span>}
                        </td>
                        <td className="px-4 py-3">
                          {!action && (
                            <div className="flex gap-2">
                              <button onClick={() => handleAccept(item)}
                                className="text-xs bg-zoddle-green text-white px-3 py-1 rounded-lg hover:opacity-90 font-semibold">
                                Accept
                              </button>
                              <button onClick={() => handleReject(item)}
                                className="text-xs border border-zoddle-pink text-zoddle-pink px-3 py-1 rounded-lg hover:bg-red-50 font-semibold">
                                Reject
                              </button>
                              <button onClick={() => handleMissing(item)}
                                className="text-xs border border-amber-500 text-amber-700 px-3 py-1 rounded-lg hover:bg-amber-50 font-semibold">
                                Missing
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* PUTAWAY TAB */}
      {activeTab === 'putaway' && (
        <div>
          {/* Filter bar + actions */}
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <select value={putawayFilter.gender} onChange={(e) => setPutawayFilter(p => ({...p, gender: e.target.value}))}
              className="text-sm border border-gray-200 rounded-lg px-3 py-2 text-gray-600 bg-white focus:outline-none focus:border-zoddle-green">
              <option value="">All Genders</option>
              <option value="girl">Girl</option>
              <option value="boy">Boy</option>
            </select>
            <select value={putawayFilter.size} onChange={(e) => setPutawayFilter(p => ({...p, size: e.target.value}))}
              className="text-sm border border-gray-200 rounded-lg px-3 py-2 text-gray-600 bg-white focus:outline-none focus:border-zoddle-green">
              <option value="">All Sizes</option>
              {zoddleSizes.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <select value={putawayFilter.inbound_batch} onChange={(e) => setPutawayFilter(p => ({...p, inbound_batch: e.target.value}))}
              className="text-sm border border-gray-200 rounded-lg px-3 py-2 text-gray-600 bg-white focus:outline-none focus:border-zoddle-green">
              <option value="">All Batches</option>
              {putawayBatches.map(b => <option key={b} value={b}>{b}</option>)}
            </select>
            {(putawayFilter.gender||putawayFilter.size||putawayFilter.inbound_batch) && (
              <button onClick={() => setPutawayFilter({size:'',gender:'',inbound_batch:''})}
                className="text-sm text-gray-400 hover:text-gray-600">Clear</button>
            )}
            <div className="flex-1" />
            <p className="text-gray-400 text-sm">{putawayItems.length} items</p>
            <button onClick={handleBulkPutawayAll} disabled={bulkPutawayAllRunning||putawayItems.length===0}
              className="bg-zoddle-green text-white text-sm font-semibold px-4 py-2 rounded-lg hover:opacity-90 disabled:opacity-50">
              {bulkPutawayAllRunning ? 'Putting away...' : `Putaway All (${putawayItems.filter(i=>i.suggested_bin_id).length} with suggestions)`}
            </button>
          </div>

          {/* Bulk putaway all result */}
          {bulkPutawayAllResult && (
            <div className="mb-4 bg-green-50 border border-green-100 rounded-xl p-4">
              <p className="font-semibold text-green-800">✓ {bulkPutawayAllResult.total_put_away} items put away</p>
              {bulkPutawayAllResult.total_skipped > 0 && (
                <p className="text-sm text-yellow-700 mt-1">{bulkPutawayAllResult.total_skipped} skipped — no bin suggestion available</p>
              )}
            </div>
          )}

          {/* Items table */}
          {putawayLoading ? (
            <p className="text-gray-400 text-sm">Loading...</p>
          ) : putawayItems.length === 0 ? (
            <p className="text-gray-400 text-sm">No items awaiting putaway.</p>
          ) : (
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-6">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100">
                    {['Image','Barcode','Brand · Design','Gender','Size','Batch','Suggested Bin','Action'].map(h => (
                      <th key={h} className="text-left px-4 py-3 text-gray-500 font-semibold">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {putawayItems.map(item => {
                    const sizeLabel = item.brand_size
                      ? `${item.size || '—'} (${item.brand_size})`
                      : (item.size || '—');
                    return (
                    <tr key={item.item_id} className="border-b border-gray-50 hover:bg-gray-50">
                      <td className="px-4 py-3">
                        {item.image_url_1 ? (
                          <a href={item.image_url_1} target="_blank" rel="noopener noreferrer" title="Open full image">
                            <img src={cldThumb(item.image_url_1, 300)} alt={item.design_name || ''}
                              className="w-12 h-12 object-cover rounded border border-gray-200 hover:opacity-80"
                              loading="lazy"
                              onError={e => { e.currentTarget.style.display = 'none'; }} />
                          </a>
                        ) : <span className="text-gray-300 text-xs">—</span>}
                      </td>
                      <td className="px-4 py-3 text-xs font-mono text-gray-700">
                        {item.zoddle_barcode || <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-xs text-gray-500">{item.brand_name || '—'}</p>
                        <p className="font-semibold text-gray-800 text-xs">{item.design_name}</p>
                      </td>
                      <td className="px-4 py-3 text-gray-600 capitalize text-xs">{item.gender}</td>
                      <td className="px-4 py-3 text-gray-600 text-xs">{sizeLabel}</td>
                      <td className="px-4 py-3 text-gray-500 text-xs">{item.inbound_batch}</td>
                      <td className="px-4 py-3">
                        {item.suggested_bin_label ? (
                          <span className="text-xs bg-green-50 text-green-700 px-2 py-1 rounded-full font-medium font-mono">
                            {item.suggested_bin_label}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">No suggestion</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => handlePutawayOne(item)}
                          disabled={puttingAway[item.item_id] || !item.suggested_bin_id}
                          className="text-xs bg-zoddle-green text-white px-3 py-1 rounded-lg hover:opacity-90 disabled:opacity-40 font-semibold">
                          {puttingAway[item.item_id] ? '...' : 'Put Away'}
                        </button>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Bulk putaway via CSV */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="font-bold text-gray-800 text-sm">Bulk Putaway via CSV</h3>
                <p className="text-xs text-gray-500 mt-0.5">Upload CSV with item_id + optional bin_id</p>
              </div>
              <button onClick={downloadPutawayTemplate}
                className="text-xs text-zoddle-blue border border-zoddle-blue rounded-lg px-3 py-1.5 hover:bg-blue-50 font-semibold">
                Download Template
              </button>
            </div>
            <input type="file" accept=".csv" onChange={handleBulkPutawayFile} disabled={bulkPutawayFileRunning}
              className="w-full text-sm text-gray-600 border border-gray-200 rounded-lg px-3 py-2 bg-gray-50
                         file:mr-3 file:py-1 file:px-3 file:rounded-lg file:border-0 file:text-sm
                         file:font-semibold file:bg-zoddle-blue file:text-white hover:file:opacity-90" />
            {bulkPutawayFileRunning && <p className="text-sm text-gray-500 mt-2">Processing...</p>}
            {bulkPutawayFileResult && (
              <div className="mt-3 bg-green-50 rounded-lg p-3">
                <p className="text-sm font-semibold text-green-800">✓ {bulkPutawayFileResult.total_put_away} items put away</p>
                {bulkPutawayFileResult.total_skipped > 0 && (
                  <div className="mt-2">
                    <p className="text-xs text-yellow-700 font-semibold">{bulkPutawayFileResult.total_skipped} skipped:</p>
                    {bulkPutawayFileResult.skipped.map((s,i) => (
                      <p key={i} className="text-xs text-gray-500">{s.item_id.substring(0,8)}... — {s.reason}</p>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* REJECTED TAB */}
      {activeTab === 'rejected' && (
        <div>
          <div className="flex items-center gap-3 mb-4">
            <select value={rejectedBatchFilter} onChange={(e) => setRejectedBatchFilter(e.target.value)}
              className="text-sm border border-gray-200 rounded-lg px-3 py-2 text-gray-600 bg-white focus:outline-none focus:border-zoddle-green">
              <option value="">All Batches</option>
              {allBatches.map(b => <option key={b} value={b}>{b}</option>)}
            </select>
            {rejectedBatchFilter && (
              <button onClick={() => setRejectedBatchFilter('')} className="text-sm text-gray-400 hover:text-gray-600">Clear</button>
            )}
            <div className="flex-1" />
            {rejectedBatchFilter && rejectedItems.length > 0 && (
              <button
                onClick={() => downloadRejectedReport(rejectedBatchFilter).catch(e => alert(e.response?.data?.detail || 'Failed'))}
                className="text-sm bg-zoddle-blue text-white font-semibold px-4 py-2 rounded-lg hover:opacity-90">
                Download {rejectedBatchFilter}_rejected_report.csv
              </button>
            )}
            <p className="text-gray-400 text-sm">{rejectedItems.length} item{rejectedItems.length!==1?'s':''}</p>
          </div>
          {rejectedLoading ? (
            <p className="text-gray-400 text-sm">Loading...</p>
          ) : rejectedItems.length === 0 ? (
            <p className="text-gray-400 text-sm">No rejected/missing items found.</p>
          ) : (
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100">
                    {['Image','Zoddle Barcode','Brand · Design','Size','Batch','MRP','Cost','At'].map(h => (
                      <th key={h} className="text-left px-4 py-3 text-gray-500 font-semibold">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rejectedItems.map(item => {
                    const sizeLabel = item.brand_size
                      ? `${item.size || '—'} (${item.brand_size})`
                      : (item.size || '—');
                    const isMissing = item.status === 'missing';
                    return (
                    <tr key={item.item_id} className={`border-b border-gray-50 hover:bg-gray-50 ${isMissing ? 'bg-amber-50/40' : ''}`}>
                      <td className="px-4 py-3">
                        {item.image_url_1 ? (
                          <a href={item.image_url_1} target="_blank" rel="noopener noreferrer" title="Open full image">
                            <img src={cldThumb(item.image_url_1, 300)} alt={item.design_name || ''}
                              className="w-12 h-12 object-cover rounded border border-gray-200 hover:opacity-80"
                              loading="lazy"
                              onError={e => { e.currentTarget.style.display = 'none'; }} />
                          </a>
                        ) : <span className="text-gray-300 text-xs">—</span>}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-gray-700">
                        {item.zoddle_barcode || '—'}
                        {item.brand_barcode && <span className="text-gray-400"> ({item.brand_barcode})</span>}
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-xs text-gray-500">{item.brand_name}</p>
                        <p className="font-semibold text-gray-800 text-xs">
                          {item.design_name}
                          {isMissing && (
                            <span className="ml-2 text-xxs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium uppercase">missing</span>
                          )}
                        </p>
                      </td>
                      <td className="px-4 py-3 text-gray-600 text-xs">{sizeLabel}</td>
                      <td className="px-4 py-3 text-gray-500 text-xs">{item.inbound_batch}</td>
                      <td className="px-4 py-3 text-gray-700">₹{item.mrp}</td>
                      <td className="px-4 py-3 text-gray-500">₹{item.cost_price}</td>
                      <td className="px-4 py-3 text-gray-400 text-xs">
                        {new Date(item.rejected_at).toLocaleDateString('en-IN', {
                          day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit'
                        })}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* WAREHOUSE TAB */}
      {activeTab === 'warehouse' && <Warehouse />}
    </div>
  );
}
